import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { CleanCopiesAnswer } from 'fym-shared/src/music24/watermarkUpsell';
import { TypedMemo } from '../../../common/types';
import { useAccountId } from '../../../../redux/slices/appInfo/hooks';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLButton } from '../../components/primitives/SMLButton';
import { planNameMessages } from '../../config/music24PlanNames';
import { useMusic24Plan } from '../../hooks/useMusic24Plan';
import { useOpenReleasePage } from '../../promote/releases/useOpenReleasePage';
import { cleanCopiesMessages as messages } from './cleanCopiesMessages';
import { CleanCopyRow } from './CleanCopyRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CleanCopiesViewProps {
  cleanCopies: CleanCopiesAnswer;
}

/**
 * The clean copies after the payment (design: trial-upsell b5), on the payment return page: while
 * the run makes the copies, the progress and one row for each file; then the end state. A file that
 * the plan cannot make stays a preview and says why; a failed copy says so. The applied cover of a
 * song is drawn in the browser, so the cover editor must export it again.
 */
const CleanCopiesViewComponent: React.FC<CleanCopiesViewProps> = ({ cleanCopies }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const accountId = useAccountId();
  const plan = useMusic24Plan();
  const openRelease = useOpenReleasePage({ accountId });
  const { status, total, done, scheduledPosts, catalogItemUuid, songTitle, items } = cleanCopies;
  const openSong = useCallback(() => {
    if (catalogItemUuid === null) {
      router.push(SMLRoutes.promote);
      return;
    }
    openRelease(catalogItemUuid);
  }, [catalogItemUuid, openRelease, router]);
  const openPosts = useCallback(() => router.push(SMLRoutes.promotePosts), [router]);
  const isDone = status === 'done';
  // The end state lists only the files that did not get a clean copy.
  const notClean = useMemo(() => items.filter((item) => item.state !== 'clean'), [items]);
  const cover = cleanCopies.coverHasWatermark && (
    <p className={styles.fine} data-testid="clean-copies-cover">
      {intl.formatMessage(messages.coverLine)}
    </p>
  );

  if (isDone) {
    let body = intl.formatMessage(messages.doneBody);
    if (scheduledPosts > 0) {
      body = `${body} ${intl.formatMessage(messages.doneBodyPosts, { count: scheduledPosts })}`;
    }
    return (
      <div className={`${styles.modal} ${styles.cleanCard}`} data-testid="clean-copies-done">
        <div className={styles.eyebrow}>{intl.formatMessage(messages.doneEyebrow)}</div>
        <div>
          <h3 className={styles.h}>
            {done >= total
              ? intl.formatMessage(messages.doneTitle, { count: total })
              : intl.formatMessage(messages.doneTitlePart, { done, total })}
          </h3>
          <p className={styles.p}>{body}</p>
        </div>
        {done < total && (
          <div className={`${styles.rows} ${styles.rowsScroll}`}>
            {notClean.map((item) => (
              <CleanCopyRow key={`${item.kind}:${item.uuid}`} item={item} />
            ))}
          </div>
        )}
        {cover}
        <div className={styles.acts}>
          <SMLButton variant="primary" onClick={openSong}>
            {intl.formatMessage(messages.downloadKit)}
          </SMLButton>
          <SMLButton onClick={openPosts}>{intl.formatMessage(messages.postThem)}</SMLButton>
        </div>
      </div>
    );
  }

  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className={`${styles.modal} ${styles.cleanCard}`} data-testid="clean-copies-running">
      <div className={styles.eyebrow}>
        {intl.formatMessage(messages.eyebrowPlan, { plan: intl.formatMessage(planNameMessages[plan]) })}
      </div>
      <div>
        <h3 className={styles.h}>{intl.formatMessage(messages.runningTitle, { count: total })}</h3>
        <p className={styles.p}>{intl.formatMessage(messages.runningBody)}</p>
      </div>
      <div className={styles.progress}>
        <div className={styles.progressTop}>
          <span>{intl.formatMessage(messages.progressLabel)}</span>
          <span>{intl.formatMessage(messages.progressValue, { done, total })}</span>
        </div>
        <div className={styles.meter} role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${percent}%` }} />
        </div>
      </div>
      <div className={`${styles.rows} ${styles.rowsScroll}`}>
        {items.map((item) => (
          <CleanCopyRow key={`${item.kind}:${item.uuid}`} item={item} />
        ))}
      </div>
      {cover}
      <div className={styles.acts}>
        <SMLButton variant="primary" onClick={openSong}>
          {songTitle === null
            ? intl.formatMessage(messages.openRelease)
            : intl.formatMessage(messages.openSong, { song: songTitle })}
        </SMLButton>
        {scheduledPosts > 0 && (
          <span className={styles.fine}>{intl.formatMessage(messages.postsRunning, { count: scheduledPosts })}</span>
        )}
      </div>
    </div>
  );
};

export const CleanCopiesView = TypedMemo(CleanCopiesViewComponent);
